import { useAuth0 } from '@auth0/auth0-react';
import { memo, MouseEvent, useState } from 'react';

import { useInstance } from '@mint-lib/admin-routing-context';
import { useTranslation } from '@mint-lib/i18n';
import { useMutation } from '@mint-lib/query-client';
import { SlotsProvider } from '@mint-lib/slots';
import type {} from '@mint-lib/theme/src/extensions/palette.js';
import { Session } from '@mint-sdk/api-admin';
import { Outlet, useLocation } from '@mint/admin-routing';

import { Box, Icon, PopoverItem, SideBar } from '@myn/mui';

import { OfflineNetworkStatusAlert } from '../features/OfflineNetworkStatusAlert.jsx';
import SideBarComponent from '../features/SideBar.jsx';
import TopBar from '../features/TopBar.jsx';

export default memo(function IndexPage() {
  const { t } = useTranslation('@mint-admin/secured-layout');
  const [auth] = useInstance('auth');
  const location = useLocation();
  const { logout } = useAuth0();
  const { mutateAsync: logoutRequest } = useMutation({
    mutationFn: Session.logout,
  });
  const nextUrl = encodeURIComponent(location.pathname);
  const [isPopoverOpen, setIsPopoverOpen] = useState<HTMLButtonElement | null>(
    null,
  );

  const handleClick = (event: MouseEvent<HTMLButtonElement | null>) => {
    setIsPopoverOpen(event.currentTarget);
  };
  const handlePopoverClose = () => setIsPopoverOpen(null);
  const handleLogout = async () => {
    handlePopoverClose();

    await logoutRequest({});
    await auth?.logout();
    await logout({
      logoutParams: { returnTo: `${window.location.origin}?next=${nextUrl}` },
    });
  };

  return (
    <SlotsProvider>
      <OfflineNetworkStatusAlert />
      <Box
        sx={{
          display: 'grid',
          gridTemplateRows: '48px calc(100vh - 48px)',
        }}
      >
        <TopBar
          data-hj-suppress
          onAvatarClick={handleClick}
          onPopoverClose={handlePopoverClose}
          isPopoverOpen={isPopoverOpen}
          popoverContent={
            <PopoverItem onItemClick={handleLogout} text={t('Logout')} />
          }
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(min-content, max-content) auto',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '48px minmax(min-content, max-content)',
              overflow: 'auto',
            }}
          >
            <SideBar
              logo={
                <Icon
                  sx={{
                    width: '100%',
                    height: '100%',
                  }}
                  component="Monogram"
                />
              }
              topIcons={[]} // TODO: add icons
            />
            <SideBarComponent />
          </Box>
          <Box
            sx={{
              display: 'grid',
              overflow: 'auto',
            }}
          >
            <Box
              id="page-wrapper"
              sx={{
                overflow: 'auto',
                padding: '24px 24px',
                boxSizing: 'border-box',
                height: '100%',
              }}
            >
              <Outlet />
            </Box>
          </Box>
        </Box>
      </Box>
      <div id="dialog-portal" />
    </SlotsProvider>
  );
});
